/**
 * Sequência guiada de cancelamento de expedição — TMS-8133 (ação A3, defeito D1).
 *
 * Cancelar uma expedição no iSend nunca foi uma ação: é uma sequência de quatro
 * passos em ordem obrigatória que só existia como conhecimento tácito do Service
 * Desk. Este módulo é a única fonte dessa ordem no front.
 *
 * A ordem importa por um motivo verificado no código do backend: `estornarCalculo`
 * grava `status_processo_expedicao = EmAndamento (2)`, e a guarda de "Cancelar
 * NSTech" exige `== 3`. Quem estorna primeiro perde o passo anterior, e o único
 * código que devolve 3 é o da própria expedição — para reabrir o passo 1 seria
 * preciso expedir de novo a carga que se queria cancelar. Por isso "Cancelar
 * NSTech" é oferecido ANTES de "Estornar cálculo", e nenhum passo oferecido pode
 * tornar um passo anterior inacessível.
 *
 * O módulo é puro de propósito (sem DOM, sem Vue, sem axios): é a decisão de
 * apresentação que precisa de teste, e a view apenas consome. Ver jest.config.js.
 *
 * Contrato de entrada do bloqueio: `isend-backend`, ADR-0005, decisão D-01 —
 * envelope legado intacto + a chave de topo `bloqueio` com 9 chaves fixas.
 */

import { StatusProcessoExpedicaoEnum } from "../../enums/GeneralEnums";
import { decodificarEntidadesHtml } from "./entidadesHtml";

/* Códigos dos passos da sequência — espelham os códigos de `proxima_acao`
 * publicados pelo backend. Não inventar código novo aqui sem o par no back. */
export const CodigoPasso = Object.freeze({
  CANCELAR_NSTECH: "cancelar_carga_nstech",
  ESTORNAR_CALCULO: "estornar_calculo",
  CANCELAR_VALE_PEDAGIO: "cancelar_vale_pedagio",
  CANCELAR_EXPEDICAO: "cancelar_expedicao",
});

/* Ações que o modal pode oferecer: os passos da sequência mais as saídas que
 * não fazem parte dela (acompanhar, voltar para montagem, abrir chamado). */
export const CodigoAcao = Object.freeze({
  ...CodigoPasso,
  /* `cancelar_ciot` é publicado por `ProximaAcaoBloqueio` (guardas CiotAtivo e
     EstornoCiotAtivo) e não é passo da sequência de 4 — é a saída de uma família
     própria. Sem entrada aqui e sem handler no registry, o backend ofereceria a
     ação e a tela responderia "ainda não está disponível" (TMS-8134). */
  CANCELAR_CIOT: "cancelar_ciot",
  VOLTAR_PARA_MONTAGEM: "voltar_para_montagem",
  ACOMPANHAR_STATUS: "acompanhar_status",
  VER_OBJETO: "ver_objeto",
  ABRIR_CHAMADO: "abrir_chamado",
});

export const MotivoIndisponibilidade = Object.freeze({
  SEM_PERMISSAO: "sem_permissao",
  ESTADO_INCOMPATIVEL: "estado_incompativel",
  PASSO_ANTERIOR_PENDENTE: "passo_anterior_pendente",
  PRAZO_EXPIRADO: "prazo_expirado",
  /* Estornar o cálculo não libera o cancelamento enquanto o Vale Pedágio estiver
     fora da janela: o passo existe, mas oferecê-lo como "próximo" contradiz o
     texto do modal e leva o operador a um beco. */
  ESTORNO_NAO_RESOLVE: "estorno_nao_resolve",
});

/**
 * Permissão que o backend realmente exige em cada passo — fonte única do front,
 * consumida também por `utils/expedicaoRowVisibility.js` (gates da grid).
 *
 * Espelha `ProximaAcaoBloqueio.php` do isend-backend. Cada valor foi conferido
 * na rota correspondente (TMS-8135):
 *   cancelar_carga_nstech  DELETE /api/nstech/cancelar-carga/{id}
 *                          Modules/Embarcador/Routes/api.php — grupo
 *                          `permission:inventario.index`
 *   estornar_calculo       POST   /api/expedicao/calculo/estornar   api.php:2289-2296
 *   cancelar_vale_pedagio  POST   /api/expedicao/integracao/manual  api.php:2305-2312
 *                          (tipo 6) — `permission:expedicao.index`
 *   cancelar_expedicao     POST   /api/expedicao/cancelar           api.php:2236-2243
 *   voltar_para_montagem   PATCH  /api/montagemcarga/cancelar       api.php:2037-2044
 *
 * `expedicao.store` NÃO gateia nenhum passo do cancelamento: é a permissão de
 * `POST /api/expedicao/store` (confirmar expedição). Usá-la aqui fazia a tela
 * oferecer ações que o backend recusa com 403.
 */
export const PermissaoPasso = Object.freeze({
  [CodigoPasso.CANCELAR_NSTECH]: "inventario.index",
  [CodigoPasso.ESTORNAR_CALCULO]: "expedicao.calculo.estornar",
  [CodigoPasso.CANCELAR_VALE_PEDAGIO]: "expedicao.index",
  [CodigoPasso.CANCELAR_EXPEDICAO]: "expedicao.cancelar",
  [CodigoAcao.VOLTAR_PARA_MONTAGEM]: "montagemcarga.update",
});

const ROTULO_PASSO = Object.freeze({
  [CodigoPasso.CANCELAR_NSTECH]: "Cancelar a carga na NSTech",
  [CodigoPasso.ESTORNAR_CALCULO]: "Estornar o cálculo de despesa",
  [CodigoPasso.CANCELAR_VALE_PEDAGIO]: "Cancelar o Vale Pedágio",
  [CodigoPasso.CANCELAR_EXPEDICAO]: "Cancelar a expedição",
});

/* A ordem obrigatória, como lista — é o que permite responder "este passo vem
 * antes daquele?" sem depender do `ordem` recalculado de `passosPendentes`
 * (que é o índice na lista de pendências, não a posição na sequência). */
const ORDEM_DA_SEQUENCIA = Object.freeze([
  CodigoPasso.CANCELAR_NSTECH,
  CodigoPasso.ESTORNAR_CALCULO,
  CodigoPasso.CANCELAR_VALE_PEDAGIO,
  CodigoPasso.CANCELAR_EXPEDICAO,
]);

/* `statusExpedicaoNstech` nesses valores significa que já não há o que cancelar
 * do lado da NSTech. Mesmo conjunto usado hoje para exibir o botão na grid. */
const STATUS_NSTECH_SEM_PENDENCIA = Object.freeze([1, 6]);

/* ------------------------------------------------------------------------- *
 * Permissões
 * ------------------------------------------------------------------------- */

/**
 * Aceita os formatos em que a permissão chega ao front: o helper global
 * `getPermissionFw`, a lista crua do store (`[{ name }]`) ou uma lista de nomes.
 * `null`/`undefined` significa "não informado" — e nesse caso não se bloqueia por
 * permissão, porque esconder passo por falta de dado seria pior do que mostrá-lo.
 *
 * @param {Function|Array|null|undefined} permissoes
 * @returns {function(string): boolean}
 */
function verificadorDePermissao(permissoes) {
  if (typeof permissoes === "function") {
    return (nome) => Boolean(permissoes(nome));
  }

  if (Array.isArray(permissoes)) {
    const nomes = permissoes.map((item) =>
      item && typeof item === "object" ? item.name : item
    );
    return (nome) => nomes.indexOf(nome) >= 0;
  }

  return () => true;
}

/* ------------------------------------------------------------------------- *
 * Estado da sequência
 * ------------------------------------------------------------------------- */

function paraNumero(valor) {
  /* `Number(null)` e `Number("")` são 0 e finitos — sem este guard, relação
     ausente no resource viraria o status 0 e a tela decidiria sobre um dado
     que não existe. */
  if (valor == null || valor === "") {
    return null;
  }

  const numero = Number(valor);
  return Number.isFinite(numero) ? numero : null;
}

/**
 * Traduz a linha da grid de Expedição (`ExpedicaoResource`) para o estado que a
 * sequência entende. O resource é montado com `whenLoaded`, então qualquer
 * relação pode faltar no payload — daí a leitura toda defensiva.
 *
 * @param {object|null|undefined} rowData
 * @returns {{cargaId: number|null, statusProcessoExpedicao: number|null,
 *            dtInicioExpedido: string|null, temTrechoDespesa: boolean,
 *            temValePedagio: boolean, valePedagioExpirado: boolean,
 *            nstechPendente: boolean,
 *            valePedagio: object|null}}
 */
export function estadoDaLinha(rowData) {
  const linha = rowData && typeof rowData === "object" ? rowData : {};
  const statusProcesso = linha.statusProcessoExpedicao || {};
  const integracoes = linha.statusIntegracoes || {};
  const carregamentoDespesa =
    (integracoes.statusCarregamentoDespesa &&
      integracoes.statusCarregamentoDespesa.status) ||
    {};
  const integracaoNstech = Array.isArray(linha.integracaoNstech)
    ? linha.integracaoNstech
    : [];
  const valePedagio =
    linha.valePedagio && typeof linha.valePedagio === "object"
      ? linha.valePedagio
      : null;

  return {
    cargaId: paraNumero(linha.cargaId),
    statusProcessoExpedicao: paraNumero(statusProcesso.valor),
    /* Início do processamento da expedição (`ExpedicaoResource.dtInicioExpedido`,
       data ingênua em BRT) — vira `objeto.desde` na recusa local (CT03: "iniciada
       em <hora>"). Ausente na linha, é `null` e a copy simplesmente omite o trecho. */
    dtInicioExpedido: linha.dtInicioExpedido || null,
    /* Mesmos sinais que a grid já usa para exibir "Estornar cálculo": o
       carregamento de despesa concluído (valor 1) ou `statusExpedicaoDespesa 3`. */
    temTrechoDespesa:
      paraNumero(carregamentoDespesa.valor) === 1 ||
      paraNumero(linha.statusExpedicaoDespesa) === 3,
    temValePedagio: linha.cargaValePedagio != null,
    valePedagioExpirado: Boolean(valePedagio && valePedagio.expirado),
    valePedagio,
    nstechPendente:
      integracaoNstech.length > 0 &&
      STATUS_NSTECH_SEM_PENDENCIA.indexOf(
        paraNumero(linha.statusExpedicaoNstech)
      ) < 0,
  };
}

/**
 * Etapa e motivo do erro de expedição, lidos de `integracaoLog` — a mesma
 * relação que alimenta a aba Erros e onde a TMS-8129 passou a gravar a recusa.
 * É a única fonte de motivo disponível na linha; o motivo vem integral e a tela
 * não o trunca (no SDK-101072 a coluna mostrava "Rejeição: IE d…").
 *
 * @param {object|null|undefined} rowData
 * @returns {{etapa: string|null, motivo: string|null}}
 */
export function erroDaLinha(rowData) {
  const linha = rowData && typeof rowData === "object" ? rowData : {};
  const registros = Array.isArray(linha.integracaoLog) ? linha.integracaoLog : [];

  const ultimo = registros
    .filter((registro) => registro && registro.descr)
    .reduce((maisRecente, registro) => {
      if (!maisRecente) {
        return registro;
      }
      const atual = String(registro.dtUltimaOcorrencia || "");
      const anterior = String(maisRecente.dtUltimaOcorrencia || "");
      return atual >= anterior ? registro : maisRecente;
    }, null);

  if (!ultimo) {
    return { etapa: null, motivo: null };
  }

  return {
    etapa: ultimo.integracaoDescr || null,
    motivo: ultimo.descr || null,
  };
}

function montarPasso(codigo, ordem, motivoIndisponivel) {
  return {
    codigo,
    rotulo: ROTULO_PASSO[codigo],
    ordem,
    disponivel: motivoIndisponivel == null,
    motivoIndisponivel: motivoIndisponivel == null ? null : motivoIndisponivel,
  };
}

/**
 * Passos que ainda faltam para que o cancelamento da expedição seja aceito,
 * na ordem em que precisam ser executados.
 *
 * @param {object} [estado] saída de `estadoDaLinha` acrescida de `permissoes`
 * @returns {Array<{codigo: string, rotulo: string, ordem: number,
 *                  disponivel: boolean, motivoIndisponivel: string|null}>}
 */
export function passosPendentes(estado) {
  const dados = estado && typeof estado === "object" ? estado : {};
  const pode = verificadorDePermissao(dados.permissoes);
  const passos = [];

  /* 1 — Cancelar NSTech. Vem primeiro porque o passo 2 o desabilita. */
  if (dados.nstechPendente) {
    let motivo = null;
    if (
      paraNumero(dados.statusProcessoExpedicao) !==
      StatusProcessoExpedicaoEnum.CONCLUIDO
    ) {
      motivo = MotivoIndisponibilidade.ESTADO_INCOMPATIVEL;
    } else if (!pode(PermissaoPasso[CodigoPasso.CANCELAR_NSTECH])) {
      motivo = MotivoIndisponibilidade.SEM_PERMISSAO;
    }
    passos.push(montarPasso(CodigoPasso.CANCELAR_NSTECH, 1, motivo));
  }

  /* 2 — Estornar cálculo. Só é ofertável se o passo 1 já não estiver ao alcance:
     executá-lo leva a carga a status 2 e queima o "Cancelar NSTech". Quando o
     passo 1 está pendente mas já é inexecutável, travar aqui só criaria um novo
     impasse — então libera. */
  if (dados.temTrechoDespesa) {
    const nstechAindaAlcancavel = passos.some(
      (passo) => passo.codigo === CodigoPasso.CANCELAR_NSTECH && passo.disponivel
    );

    let motivo = null;
    if (nstechAindaAlcancavel) {
      motivo = MotivoIndisponibilidade.PASSO_ANTERIOR_PENDENTE;
    } else if (dados.temValePedagio && dados.valePedagioExpirado) {
      motivo = MotivoIndisponibilidade.ESTORNO_NAO_RESOLVE;
    } else if (!pode(PermissaoPasso[CodigoPasso.ESTORNAR_CALCULO])) {
      motivo = MotivoIndisponibilidade.SEM_PERMISSAO;
    }
    passos.push(montarPasso(CodigoPasso.ESTORNAR_CALCULO, 2, motivo));
  }

  /* 3 — Cancelar Vale Pedágio. A janela é do fornecedor (Target) e o limite vem
     de b_config_blogistic — nunca é 3h fixas nem decidido aqui. */
  if (dados.temValePedagio) {
    let motivo = null;
    if (dados.valePedagioExpirado) {
      motivo = MotivoIndisponibilidade.PRAZO_EXPIRADO;
    } else if (!pode(PermissaoPasso[CodigoPasso.CANCELAR_VALE_PEDAGIO])) {
      motivo = MotivoIndisponibilidade.SEM_PERMISSAO;
    }
    passos.push(montarPasso(CodigoPasso.CANCELAR_VALE_PEDAGIO, 3, motivo));
  }

  /* 4 — Cancelar expedição. A guarda do backend recusa enquanto existir qualquer
     bloqueador, disponível ou não — então aqui é fato, não preferência. */
  let motivoCancelar = null;
  if (passos.length > 0) {
    motivoCancelar = MotivoIndisponibilidade.PASSO_ANTERIOR_PENDENTE;
  } else if (!pode(PermissaoPasso[CodigoPasso.CANCELAR_EXPEDICAO])) {
    motivoCancelar = MotivoIndisponibilidade.SEM_PERMISSAO;
  }
  passos.push(montarPasso(CodigoPasso.CANCELAR_EXPEDICAO, 4, motivoCancelar));

  return passos.map((passo, indice) => ({ ...passo, ordem: indice + 1 }));
}

/**
 * O passo que a tela deve oferecer agora. `null` quando nenhum é executável —
 * caso do perfil "recebimento", que tem `expedicao.cancelar` e não tem
 * `expedicao.calculo.estornar`. Nesse caso a tela precisa dizer isso, e não
 * oferecer um botão que devolve 403.
 *
 * @param {object} [estado]
 * @returns {object|null}
 */
export function proximoPasso(estado) {
  return passosPendentes(estado).find((passo) => passo.disponivel) || null;
}

/**
 * Cruza a ação que o backend ofereceu com a sequência que a tela está exibindo.
 *
 * Existe porque `ProximaAcaoBloqueio.php` devolve `estornar_calculo` para
 * `TrechoDespesaAtivo` **incondicionalmente**: ele responde "qual ação desbloqueia
 * esta guarda", não "qual ação é segura agora". Quando a carga também tem a NSTech
 * pendente, estornar grava `status_processo_expedicao = 2` e a guarda do
 * cancelamento na NSTech exige 3 — o passo 1 é queimado, e só reexpedindo a carga
 * voltaria a ser alcançável. Era o defeito D1 reaparecendo no ponto exato da ação:
 * a lista dizia "depende de um passo anterior" e o mesmo modal oferecia o botão.
 *
 * Só ações que **são** passos da sequência entram nesse juízo. `voltar_para_montagem`,
 * `acompanhar_status` e `cancelar_ciot` são saídas de outras famílias, não têm posição
 * nesta ordem e passam intactas.
 *
 * Quando o front não enxergou o passo na linha (o `ExpedicaoResource` é montado com
 * `whenLoaded` e qualquer relação pode faltar) e não há passo anterior alcançável, a
 * palavra do backend vale: ele é a autoridade sobre o que bloqueia, e negar a única
 * saída por falta de dado local criaria um beco novo.
 *
 * @param {string|null|undefined} codigoAcao código de `proxima_acao`
 * @param {Array|null|undefined} passos saída de `passosPendentes`
 * @returns {{emSequencia: boolean, passoBloqueador: object|null,
 *            substituto: object|null, passoDaAcao: object|null}}
 */
export function avaliarAcaoNaSequencia(codigoAcao, passos) {
  const lista = Array.isArray(passos) ? passos : [];
  const livre = {
    emSequencia: true,
    passoBloqueador: null,
    substituto: null,
    passoDaAcao: null,
  };

  const indiceAcao = ORDEM_DA_SEQUENCIA.indexOf(codigoAcao);
  if (indiceAcao < 0) {
    return livre;
  }

  const proximo = lista.find((passo) => passo.disponivel) || null;
  if (proximo && proximo.codigo === codigoAcao) {
    return livre;
  }

  const passoDaAcao = lista.find((passo) => passo.codigo === codigoAcao) || null;
  /* Há um passo executável que vem ANTES na ordem obrigatória: executar a ação
     agora o tornaria inalcançável. */
  const anteriorAlcancavel = Boolean(
    proximo && ORDEM_DA_SEQUENCIA.indexOf(proximo.codigo) < indiceAcao
  );
  /* A própria lista exibida ao operador declara este passo indisponível. */
  const contraditaOModal = Boolean(passoDaAcao && !passoDaAcao.disponivel);

  if (!anteriorAlcancavel && !contraditaOModal) {
    return livre;
  }

  return {
    emSequencia: false,
    passoBloqueador: proximo || passoDaAcao || lista[0] || null,
    /* Oferecer "Cancelar a expedição" como substituto seria reoferecer a ação que
       acabou de ser recusada — o operador cairia no mesmo modal. Nesse caso a
       saída é a ação alternativa ("Abrir chamado com estes dados"). */
    substituto:
      proximo && proximo.codigo !== CodigoPasso.CANCELAR_EXPEDICAO
        ? proximo
        : null,
    passoDaAcao,
  };
}

/* ------------------------------------------------------------------------- *
 * Datas e prazo
 * ------------------------------------------------------------------------- */

/**
 * Formata o ISO 8601 do backend em `DD/MM/YYYY HH:mm` lendo a própria string.
 * Não usa `Date`: o payload já vem com o offset de São Paulo e converter para o
 * fuso do navegador mudaria a hora que o operador vai citar no chamado.
 *
 * @param {string|null|undefined} iso
 * @returns {string}
 */
export function formatarDataHora(iso) {
  if (typeof iso !== "string") {
    return "";
  }

  const partes = iso.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (!partes) {
    return "";
  }

  return `${partes[3]}/${partes[2]}/${partes[1]} ${partes[4]}:${partes[5]}`;
}

/**
 * Forma curta do tempo que falta — `2h15`, `40min`. Exportada porque a contagem
 * regressiva da grid (TMS-8134 / A6) precisa da **mesma** formatação do modal:
 * dois lugares formatando horas diferente é como o operador acaba comparando
 * números que não batem.
 *
 * @param {number|string|null|undefined} horas
 * @returns {string} vazio quando não há tempo a informar
 */
export function formatarHorasRestantes(horas) {
  const total = Number(horas);
  if (!Number.isFinite(total) || total <= 0) {
    return "";
  }

  const horasInteiras = Math.floor(total);
  const minutos = Math.round((total - horasInteiras) * 60);

  if (horasInteiras === 0) {
    return `${minutos}min`;
  }

  return `${horasInteiras}h${String(minutos).padStart(2, "0")}`;
}

/**
 * Frase do prazo. A janela é regra do fornecedor, não erro do iSend — e o limite
 * vem sempre de `prazo.limite_horas`, nunca de um 3 escrito no front.
 *
 * Sem limite conhecido não há contagem: o prazo é **indeterminado**. Hoje o backend
 * omite a chave `prazo` inteira quando não sabe o limite, mas a função não pode
 * depender disso — com `limite_horas` nulo ela imprimia "A janela de  expira em
 * 18/09 15:12 — faltam 2h30", que é uma contagem sobre uma janela que o iSend não
 * conhece (TMS-8134).
 *
 * @param {object|null|undefined} prazo
 * @returns {string}
 */
export function rotuloPrazo(prazo) {
  if (!prazo || typeof prazo !== "object") {
    return "";
  }

  const expiraEm = formatarDataHora(prazo.expira_em || prazo.expiraEm);
  const limite = paraNumero(
    prazo.limite_horas != null ? prazo.limite_horas : prazo.limiteHoras
  );
  const temLimite = limite != null && limite > 0;
  const janela = temLimite ? `${limite}h do fornecedor` : "";

  /* Expirado é fato conhecido mesmo sem o limite — e não imprime contagem. */
  if (prazo.expirado) {
    return expiraEm
      ? `A janela de ${janela || "cancelamento"} expirou em ${expiraEm}.`
      : "A janela de cancelamento do fornecedor expirou.";
  }

  if (!temLimite) {
    return (
      "O limite de cancelamento não está configurado para este cliente: o prazo " +
      "desta janela é indeterminado e o iSend não consegue dizer quanto falta."
    );
  }

  const restante = formatarHorasRestantes(
    prazo.horas_restantes != null ? prazo.horas_restantes : prazo.horasRestantes
  );

  if (expiraEm && restante) {
    return `A janela de ${janela} expira em ${expiraEm} — faltam ${restante}.`;
  }

  return expiraEm ? `A janela de ${janela} expira em ${expiraEm}.` : "";
}

/* ------------------------------------------------------------------------- *
 * Contrato de bloqueio (HTTP 422 do isend-backend)
 * ------------------------------------------------------------------------- */

/**
 * `true` quando o payload recusado traz a extensão `bloqueio`. Quando é `false`
 * a view mantém `dispatchReturnApi` — `message` é auto-suficiente por contrato.
 *
 * Precisa aguentar qualquer coisa: `Form.js:139` faz `reject(error.response.data)`
 * e, sem `error.response`, quem chega no `catch` é um `TypeError`.
 *
 * @param {*} payload
 * @returns {boolean}
 */
export function temBloqueio(payload) {
  return Boolean(
    payload &&
      typeof payload === "object" &&
      payload.bloqueio &&
      typeof payload.bloqueio === "object"
  );
}

function normalizarObjeto(objeto) {
  const dados = objeto && typeof objeto === "object" ? objeto : {};
  const ids = Array.isArray(dados.ids) ? dados.ids : [];

  return {
    tipo: dados.tipo || "",
    quantidade: paraNumero(dados.quantidade),
    ids,
    idsTruncados: Boolean(dados.ids_truncados),
    desde: dados.desde || null,
    descrLog: dados.descr_log || "",
    numControle: dados.num_controle || null,
    etapa: dados.etapa || null,
    motivo: dados.motivo || null,
    /* Cada família de guarda acrescenta campos próprios ao `objeto`
       (`num_manifesto`, `num_ciot`, `doctransp_id`, `motivos[]`, ...). A copy da
       TMS-8134 lê esses campos por nome; normalizá-los um a um aqui só criaria
       uma lista que envelhece a cada guarda nova. O bruto passa intacto. */
    bruto: dados,
  };
}

function normalizarPrazo(prazo) {
  if (!prazo || typeof prazo !== "object") {
    return null;
  }

  return {
    tipo: prazo.tipo || "",
    origem: prazo.origem || "",
    limiteHoras: paraNumero(prazo.limite_horas),
    compradoEm: prazo.comprado_em || null,
    expiraEm: prazo.expira_em || null,
    expirado: Boolean(prazo.expirado),
    horasRestantes: paraNumero(prazo.horas_restantes),
    rotulo: rotuloPrazo(prazo),
  };
}

/**
 * Traduz o envelope recusado no view-model do modal.
 *
 * A regra que dá valor a esta entrega está aqui: a ação primária só existe
 * quando `proxima_acao.permitida === true`. Para quem não tem a permissão do
 * passo, estornar, voltar para montagem e forçar NSTech devolvem 403 — oferecer
 * o botão seria trocar uma mensagem inútil por outra, e a saída é a ação
 * alternativa.
 *
 * Quantos são, medido **por usuário** e não por perfil: 20 sem o passo 2 e 2 sem
 * saída alguma (`docs/permissoes-cancelamento-expedicao.md`, seção 3). Nenhum dos
 * 23 chamados da TMS-7223 veio de falta de permissão — o valor desta regra é não
 * mentir para esses 20, não um volume.
 *
 * @param {*} payload
 * @returns {object|null}
 */
export function normalizarBloqueio(payload) {
  if (!temBloqueio(payload)) {
    return null;
  }

  const bloqueio = payload.bloqueio;
  const proximaAcao = bloqueio.proxima_acao;
  const alternativa = bloqueio.acao_alternativa;
  const permitida = Boolean(proximaAcao && proximaAcao.permitida === true);

  return {
    codigo: bloqueio.codigo || "",
    guarda: bloqueio.guarda || "",
    cargaId: paraNumero(bloqueio.carga_id),
    ocorridoEm: bloqueio.ocorrido_em || null,
    traceId: bloqueio.trace_id || "",
    titulo: payload.title || "",
    /* O `message` do backend vem escapado em HTML (o fallback do Simplert o renderiza por
       v-html). O modal renderiza por interpolação, então decodifica aqui, uma vez, para a
       copy, os detalhes e o texto do chamado receberem texto puro. */
    mensagem: decodificarEntidadesHtml(payload.message),
    objeto: normalizarObjeto(bloqueio.objeto),
    prazo: normalizarPrazo(bloqueio.prazo),
    acaoPrimaria: permitida
      ? {
          codigo: proximaAcao.codigo,
          rotulo: proximaAcao.rotulo || "",
          permissao: proximaAcao.permissao || "",
          parametros: proximaAcao.parametros || {},
          etapas: Array.isArray(proximaAcao.etapas) ? proximaAcao.etapas : [],
        }
      : null,
    motivoSemAcaoPrimaria: permitida
      ? null
      : (proximaAcao && proximaAcao.motivo_indisponibilidade) || null,
    /* A ação que existe e o usuário não pode executar. Não vira botão — vira
       frase: "seu perfil não tem permissão para «Voltar para montagem»". Dizer
       qual ação falta é o que transforma o beco em um chamado resolvível; o
       gate corrigido na TMS-8135 tirou o botão "Cancelar carga na NSTech" de 66
       usuários, e sem o nome da ação eles não teriam o que pedir. */
    acaoIndisponivel:
      !permitida && proximaAcao && typeof proximaAcao === "object"
        ? {
            codigo: proximaAcao.codigo || "",
            rotulo: proximaAcao.rotulo || "",
            permissao: proximaAcao.permissao || "",
          }
        : null,
    acaoAlternativa:
      alternativa && typeof alternativa === "object"
        ? {
            codigo: alternativa.codigo || CodigoAcao.ABRIR_CHAMADO,
            rotulo: alternativa.rotulo || "Abrir chamado com estes dados",
            dados: alternativa.dados || {},
          }
        : null,
  };
}

/**
 * Linhas do bloco "Detalhes técnicos" (recolhido) exigido pela copy.
 * Nunca devolve linha com valor vazio — detalhe em branco é ruído.
 *
 * @param {object|null} bloqueio view-model de `normalizarBloqueio`
 * @returns {Array<{rotulo: string, valor: string}>}
 */
export function detalhesTecnicos(bloqueio) {
  if (!bloqueio || typeof bloqueio !== "object") {
    return [];
  }

  const objeto = bloqueio.objeto || {};
  const linhas = [
    { rotulo: "Carga", valor: bloqueio.cargaId },
    { rotulo: "Código do bloqueio", valor: bloqueio.codigo },
    { rotulo: "Verificação que bloqueou", valor: bloqueio.guarda },
    { rotulo: "Horário", valor: formatarDataHora(bloqueio.ocorridoEm) },
    { rotulo: "Identificador da ocorrência", valor: bloqueio.traceId },
    { rotulo: "Tipo do bloqueio", valor: objeto.tipo },
    { rotulo: "Desde", valor: formatarDataHora(objeto.desde) },
    { rotulo: "Número de controle", valor: objeto.numControle },
    { rotulo: "Etapa", valor: objeto.etapa },
    { rotulo: "Motivo", valor: objeto.motivo },
    /* A permissão que falta ao perfil: o suporte precisa do código para liberar. */
    {
      rotulo: "Permissão necessária",
      valor: bloqueio.acaoIndisponivel && bloqueio.acaoIndisponivel.permissao,
    },
    /* descr_log é a ponte entre o modal e a aba Erros: é a frase gravada em
       integracao_log.descr, e é por ela que a recusa é localizável. */
    { rotulo: "Registro no histórico", valor: objeto.descrLog },
  ];

  if (objeto.ids && objeto.ids.length > 0) {
    /* O backend manda no máximo 20 ids e a quantidade real em `quantidade`.
       Omitir isso faria o operador copiar 20 ids achando que são todos. */
    const truncamento = objeto.idsTruncados
      ? ` (mostrando ${objeto.ids.length} de ${objeto.quantidade})`
      : "";
    linhas.push({
      rotulo: "Identificadores",
      valor: `${objeto.ids.join(", ")}${truncamento}`,
    });
  }

  if (bloqueio.prazo && bloqueio.prazo.rotulo) {
    linhas.push({ rotulo: "Prazo", valor: bloqueio.prazo.rotulo });
  }

  return linhas
    .filter((linha) => linha.valor != null && String(linha.valor).length > 0)
    .map((linha) => ({ rotulo: linha.rotulo, valor: String(linha.valor) }));
}

/**
 * Bloco de texto da ação alternativa "Abrir chamado com estes dados" — é o que
 * o operador cola no chamado, e é o que faz o chamado nascer rastreável.
 *
 * @param {object|null} bloqueio view-model de `normalizarBloqueio`
 * @returns {string}
 */
export function textoChamado(bloqueio) {
  if (!bloqueio || typeof bloqueio !== "object") {
    return "";
  }

  const cabecalho = bloqueio.titulo
    ? [bloqueio.titulo, bloqueio.mensagem, ""]
    : [];

  return cabecalho
    .concat(
      detalhesTecnicos(bloqueio).map((linha) => `${linha.rotulo}: ${linha.valor}`)
    )
    .join("\n")
    .trim();
}

/* ------------------------------------------------------------------------- *
 * Recusa local do "Cancelar NSTech"
 * ------------------------------------------------------------------------- */

/*
 * A recusa antiga era `statusProcessoExpedicao.valor != 3` com a frase
 * "Processo de expedição em andamento, por favor aguarde para cancelar." — a
 * mesma para Pendente (1), Em andamento (2) e Erro (4), e factualmente falsa
 * para 1 e 4. No SDK-101072 os 10 CT-es estavam em rejeição definitiva da SEFAZ:
 * não havia nada em andamento. No SDK-101098 a espera durou 29 horas.
 *
 * A recusa é local (nenhuma chamada HTTP acontece), mas é publicada no mesmo
 * shape do backend para que o modal tenha um caminho único de renderização.
 */

const GUARDA_LOCAL_NSTECH = "Expedicao.vue::onCancelarCargaNstech";

function acaoAlternativaLocal(cargaId, codigo, ocorridoEm, extras) {
  return {
    codigo: CodigoAcao.ABRIR_CHAMADO,
    rotulo: "Abrir chamado com estes dados",
    dados: {
      carga_id: cargaId,
      codigo,
      guarda: GUARDA_LOCAL_NSTECH,
      ocorrido_em: ocorridoEm,
      ...extras,
    },
  };
}

/**
 * Envelope de bloqueio para a recusa local do "Cancelar NSTech".
 * Devolve `null` quando não há recusa — o processo está Concluído (3) e a
 * chamada pode seguir — ou quando o estado é desconhecido, porque inventar
 * recusa com dado ausente é exatamente o defeito que esta task corrige.
 *
 * @param {{statusProcessoExpedicao: number|string|null, cargaId: number,
 *          etapa?: string, motivo?: string, desde?: string|null,
 *          permissoes?: Function|Array}} entrada
 * @returns {object|null} envelope compatível com `ReturnApi` + chave `bloqueio`
 */
export function bloqueioLocalNstech(entrada) {
  const dados = entrada && typeof entrada === "object" ? entrada : {};
  const status = paraNumero(dados.statusProcessoExpedicao);

  if (status === StatusProcessoExpedicaoEnum.CONCLUIDO || status == null) {
    return null;
  }

  const pode = verificadorDePermissao(dados.permissoes);
  const cargaId = paraNumero(dados.cargaId);
  const ocorridoEm = dados.ocorridoEm || null;
  const emAndamento = status === StatusProcessoExpedicaoEnum.EM_ANDAMENTO;
  const comErro = status === StatusProcessoExpedicaoEnum.ERRO;

  const title = emAndamento
    ? "A expedição ainda está processando"
    : "Não é possível cancelar na NSTech agora";

  let message;
  if (emAndamento) {
    message =
      "A expedição desta carga ainda está em processamento e o cancelamento na " +
      'NSTech só é liberado quando ela conclui. Se não concluir, use "Voltar para montagem".';
  } else if (comErro) {
    /* O motivo chega integral do backend — a copy proíbe truncar na tela. */
    const etapa = dados.etapa ? ` em ${dados.etapa}` : "";
    const motivo = dados.motivo ? `: ${dados.motivo}` : ".";
    message = `A expedição parou com erro${etapa}${motivo}`;
  } else {
    message =
      "A expedição desta carga está Pendente: não existe expedição concluída " +
      "para cancelar na NSTech.";
  }

  const codigo = emAndamento
    ? "expedicao_cancelar_nstech.processo_em_andamento"
    : "expedicao_cancelar_nstech.expedicao_nao_concluida";

  const codigoAcao = emAndamento
    ? CodigoAcao.ACOMPANHAR_STATUS
    : CodigoAcao.VOLTAR_PARA_MONTAGEM;

  const permissaoAcao = PermissaoPasso[codigoAcao] || null;
  const permitida = permissaoAcao == null ? true : pode(permissaoAcao);

  return {
    title,
    message,
    errors: "",
    type: 2,
    tp_message: "warning",
    alert_obj: { customCloseBtnText: "Fechar" },
    bloqueio: {
      codigo,
      guarda: GUARDA_LOCAL_NSTECH,
      carga_id: cargaId,
      ocorrido_em: ocorridoEm,
      trace_id: dados.traceId || "",
      objeto: {
        tipo: "carga",
        quantidade: 1,
        /* Mesmo nome que a guarda remota usa (`EstornoNstechNaoCancelada`), para
           que a copy decida o texto por um campo só. */
        status_processo_expedicao: status,
        /* "iniciada em <hora>" no estado 2 (CT03); a copy já lê `objeto.desde`. */
        desde: dados.desde || null,
        etapa: dados.etapa || null,
        motivo: dados.motivo || null,
        descr_log: "Cancelamento na NSTech recusado: expedição não concluída",
      },
      prazo: null,
      proxima_acao: {
        codigo: codigoAcao,
        rotulo: emAndamento ? "Acompanhar status" : "Voltar para montagem",
        endpoint: emAndamento ? null : "/api/montagemcarga/cancelar",
        metodo: emAndamento ? null : "PATCH",
        parametros: { carga_id: cargaId },
        permissao: permissaoAcao,
        permitida,
        motivo_indisponibilidade: permitida
          ? null
          : MotivoIndisponibilidade.SEM_PERMISSAO,
        etapas: emAndamento
          ? null
          : ["Cancelar a expedição", "Cancelar a montagem da carga"],
      },
      acao_alternativa: acaoAlternativaLocal(cargaId, codigo, ocorridoEm, {
        status_processo_expedicao: status,
        etapa: dados.etapa || null,
        motivo: dados.motivo || null,
      }),
    },
  };
}
